<template>
    <div class="mt-5">
        <information-modal
            title="Review Document"
            :show-modal="isShowReviewEditor"
            @dismiss="hideReviewEditor">
            <form @submit.prevent="reviewDocument">
                <fieldset>
                    <div class="mb-3">
                        <label
                            for="reviewStatus"
                            class="form-label"
                            >Status</label
                        >
                        <select
                            id="reviewStatus"
                            v-model="review.status"
                            class="form-select"
                            :class="{'is-invalid': !!errors.status}">
                            <option :value="null">Select Status</option>
                            <option value="DR">Reject</option>
                            <option value="DA">Accept</option>
                        </select>
                        <span
                            class="invalid-feedback"
                            role="alert"
                            v-if="errors.status">
                            {{ errors.status }}
                        </span>
                    </div>
                    <div class="mb-3">
                        <label
                            for="reviewComment"
                            class="form-label"
                            >Comment</label
                        >
                        <textarea
                            type="text"
                            v-model="review.comment"
                            id="reviewComment"
                            class="form-control">
                        </textarea>
                    </div>
                </fieldset>

                <div class="d-grid gap-2 d-md-flex justify-content-md-end">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        data-bs-dismiss="modal">
                        Cancel
                    </button>
                    <button
                        type="submit"
                        class="btn btn-primary">
                        Save
                    </button>
                </div>
            </form>
        </information-modal>
        <information-modal
            title="Request Document"
            :show-modal="isShowCreateEditor"
            ref="createDialog"
            @dismiss="hideCreateEditor">
            <form @submit.prevent="addDocument">
                <fieldset class="border-bottom mb-3">
                    <div class="mb-3 row">
                        <div class="col-md-3">
                            <label
                                for="documentName"
                                class="col-form-label"
                                >Name:</label
                            >
                        </div>
                        <div class="col-md-8">
                            <text-input
                                v-model="item.name"
                                name="name"
                                id="documentName"
                                class="form-control"
                                :error="errors.name"></text-input>
                        </div>
                    </div>
                    <div class="mb-3 row">
                        <div class="col-md-3">
                            <label
                                for="documentDescription"
                                class="col-form-label"
                                >Description:</label
                            >
                        </div>
                        <div class="col-md-8">
                            <textarea-input
                                id="documentDescription"
                                v-model="item.description"
                                class="form-control"
                                :error="errors.description"></textarea-input>
                        </div>
                    </div>
                    <div class="mb-3 row">
                        <div class="col-md-3">
                            <label
                                for="documentTemplate"
                                class="col-form-label"
                                >Document Template:</label
                            >
                        </div>
                        <div class="col-md-8">
                            <select
                                v-model="item.documentTemplateId"
                                id="documentTemplate"
                                class="form-select">
                                <option :value="null">Without Document Template</option>
                                <option
                                    v-for="item in auditDocumentTemplateStore.items"
                                    :key="item.id"
                                    :value="item.id">
                                    {{ item.name }}
                                </option>
                            </select>
                        </div>
                    </div>
                </fieldset>
                <div class="d-grid gap-2 d-md-flex justify-content-md-end">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        data-bs-dismiss="modal">
                        Cancel
                    </button>
                    <button
                        class="btn btn-primary"
                        type="submit">
                        Save
                    </button>
                </div>
            </form>
        </information-modal>

        <information-modal
            title="Upload Document"
            :show-modal="isShowUploadDocument"
            @dismiss="hideUploadDocument">
            <form @submit.prevent="uploadDocument">
                <fieldset class="border-bottom mb-3">
                    <div class="mb-3 row">
                        <div class="col-md-3">
                            <label
                                for="uploadFile"
                                class="col-form-label"
                                >File:</label
                            >
                        </div>
                        <div class="col-md-8">
                            <file-input
                                v-model="uploadDocumentRequest.file"
                                name="uploadFile"
                                :error="errors.file"></file-input>
                        </div>
                    </div>
                </fieldset>
                <div class="d-grid gap-2 d-md-flex justify-content-md-end">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        data-bs-dismiss="modal">
                        Cancel
                    </button>
                    <button
                        class="btn btn-primary"
                        type="submit">
                        Save
                    </button>
                </div>
            </form>
        </information-modal>

        <information-modal
            title="Delete"
            key="confirmDialog"
            ref="confirmDialog"
            :show-modal="isConfirmDialog"
            :show-footer="true"
            className="confirm"
            @dismiss="hideCreateEditor">
            Are You Sure?
            <template v-slot:footer>
                <button
                    type="button"
                    class="btn btn-secondary"
                    data-bs-dismiss="modal">
                    No
                </button>

                <button
                    type="button"
                    class="btn btn-primary"
                    @click="deleteDocument">
                    Yes
                </button>
            </template>
        </information-modal>

        <b-grid
            caption="List of documents"
            :tableOptions="auditDocumentStore.tableOptions"
            :filterableFields="auditDocumentStore.filterableFields"
            :filterOptions="auditDocumentStore.filterOptions"
            @optionChange="getData"
            :items="auditDocumentStore.items"
            :columns="columns"
            :loading="auditDocumentStore.loading">
            <template #beforeFilter>
                <button
                    type="button"
                    v-if="canRequestDocument"
                    class="btn btn-primary"
                    @click.prevent="showCreateEditor">
                    Request Document
                </button>
            </template>

            <template v-slot:cell.incidentNumber="{item, column}">
                <span>{{ item.incidentNumber }}</span>
            </template>

            <template v-slot:cell.status="{item, column}">
                {{ item.status }}
                <span v-if="item.statusCode !== 'DU' && item.statusCode !== 'DA'">
                    <i
                        class="bi bi-info-circle-fill text-info"
                        v-if="item.comment"
                        v-tooltip="item.comment"></i>
                </span>
            </template>
            <template v-slot:cell.name="{item, column}">
                {{ item.name }}
                <i
                    class="bi bi-info-circle-fill text-info"
                    v-if="item.description"
                    v-tooltip="item.description"></i>
            </template>
            <template v-slot:cell.review="{item, column}"> </template>

            <template v-slot:cell.template="{item, column}">
                <template v-if="item.documentTemplateFileId">
                    <span class="d-block">
                        {{ item.documentTemplateName }}
                        <small v-if="item.documentTemplateUpdatedOn"
                            >(Updated at {{ item.documentTemplateUpdatedOn }})</small
                        >
                    </span>
                    <a
                        class="btn btn-secondary btn-sm mt-2"
                        :href="`api/v1/audit/file/${item.documentTemplateFileId}`">
                        View Template
                    </a>
                </template>
                <span v-else></span>
            </template>

            <template #cell.agencyAction="{item}">
                <a
                    v-if="item.fileId"
                    class="btn btn-secondary btn-sm me-2"
                    :href="`api/v1/audit/file/${item.fileId}`"
                    download="">
                    View Document</a
                >
                <span v-else></span>

                <span v-if="item.statusCode !== 'DU' && item.statusCode !== 'DA'">
                    <button
                        type="button"
                        @click.prevent="showUploadDocument(item.id)"
                        v-if="item.statusCode == 'DN' && !runAudits"
                        class="btn btn-primary btn-sm me-2">
                        Upload Document
                    </button>
                </span>

                <button
                    type="button"
                    @click.prevent="showUploadDocument(item.id)"
                    v-if="(item.statusCode == 'DR' || item.statusCode == 'DU') && !runAudits"
                    class="btn btn-primary btn-sm me-2">
                    Replace Document
                </button>
                <span v-else></span>

                <button
                    class="btn btn-primary btn-sm me-2"
                    v-if="runAudits && item.statusCode == 'DU'"
                    @click.prevent="showReviewEditor(item.id)">
                    Provide Feedback
                </button>
                <span v-else></span>
            </template>

            <template #cell.actions="{item}">
                <button
                    @click="deleteDocumentConfirm(item.id)"
                    type="button"
                    v-if="canDelete"
                    class="btn btn-sm btn-danger">
                    Delete
                </button>
                <span v-else></span>
            </template>
        </b-grid>
    </div>
</template>
<script lang="ts" setup>
import BGrid from "../BGrid/BGrid.vue";
import {BTableColumn} from "../BTable/BTable.types";
import InformationModal from "../Misc/InformationModal.vue";

import {computed, onMounted, reactive, ref} from "vue";

import type {Audit, AuditDocument, AuditReviewDocument, UploadDocumentRequest} from "@/audit.types";
import {useAuditDocumentStore} from "@/stores/auditDocumentStore";
import {mixed, number, object, string} from "yup";
import {useFormValidation} from "@/validation";

import TextInput from "../Fields/TextInput.vue";
import TextareaInput from "../Fields/TextareaInput.vue";
import FileInput from "../Fields/FileInput.vue";
import {useAuditDocumentTemplateStore} from "@/stores/auditDocumentTemplateStore";
import {debounce} from "vue-debounce";

var updateData = debounce(() => getData(), "15000ms");
const auditDocumentStore = useAuditDocumentStore();
const auditDocumentTemplateStore = useAuditDocumentTemplateStore();

const canDelete = computed(() => props.runAudits);
const canRequestDocument = computed(() => props.runAudits && props.audit.statusCode != "AC");

const props = defineProps<{
    id: number;
    audit: Audit;
    runAudits: boolean;
    createAudits: boolean;
}>();

const review = ref({} as AuditReviewDocument);
const isShowReviewEditor = ref(false);
const isConfirmDialog = ref(false);

const selectedId = ref(0);

const deleteDocumentConfirm = async function (id) {
    isConfirmDialog.value = true;
    selectedId.value = id;
};

const columns = reactive<BTableColumn[]>([
    {
        key: "name",
        text: "Name",
        sortable: true,
        value: (item) => item["name"],
    },
    {
        key: "template",
        text: "Template",
        sortable: true,
        value: (item) => item.description,
    },
    {
        key: "status",
        text: "Status",
        sortable: false,
        value: (item) => item["status"],
    },

    {
        key: "agencyAction",
        text: "",
        sortable: false,
        value: (item) => 2,
    },

    {
        key: "actions",
        text: "",
        sortable: false,
        class: (item) => ["text-nowrap text-end"],
        value: (item) => 2,
    },
]);

const item = ref({} as AuditDocument);
const schema = object<AuditDocument>({
    name: string().required().label("Name"),
});

const {handleSubmit, errors, resetValidation} = useFormValidation();

const addDocument = async () => {
    if (await handleSubmit(schema, item.value)) {
        item.value.auditId = props.id;
        await auditDocumentStore.create(item.value);
        await getData();
        hideCreateEditor();
    }
};

const deleteDocument = async () => {
    await auditDocumentStore.deleteItem(selectedId.value);
    await getData();
    isConfirmDialog.value = false;
};
const isShowCreateEditor = ref(false);

const hideCreateEditor = () => {
    isShowCreateEditor.value = false;
};

const showCreateEditor = () => {
    resetValidation();
    isShowCreateEditor.value = true;
    item.value = {} as AuditDocument;
    item.value.documentTemplateId = null;
};

const hideUploadDocument = () => {
    isShowUploadDocument.value = false;
};

const isShowUploadDocument = ref(false);

const showUploadDocument = (id: number) => {
    resetValidation();
    uploadDocumentRequest.value = {} as UploadDocumentRequest;
    uploadDocumentRequest.value.id = id;
    isShowUploadDocument.value = true;
};

const uploadDocumentRequest = ref<UploadDocumentRequest>({} as UploadDocumentRequest);

const uploadDocumentSchema = object<UploadDocumentRequest>({
    id: number().required(),
    file: mixed().required().label("File"),
});

const uploadDocument = async () => {
    if (await handleSubmit(uploadDocumentSchema, uploadDocumentRequest.value)) {
        await auditDocumentStore.upload(uploadDocumentRequest.value);
        await getData();
        hideUploadDocument();
    }
};

const showReviewEditor = (id: number) => {
    resetValidation();
    review.value = {} as AuditReviewDocument;
    review.value.id = id;
    review.value.status = null;
    isShowReviewEditor.value = true;
};

const hideReviewEditor = () => {
    isShowReviewEditor.value = false;
};

const reviewSchema = object<AuditReviewDocument>({
    status: string().required().label("Status"),
});

const reviewDocument = async () => {
    if (await handleSubmit(reviewSchema, review.value)) {
        await auditDocumentStore.review(review.value);
        hideReviewEditor();
        await getData();
    }
};

const getData = async () => {
    await auditDocumentStore.list(props.id);
    await updateData();
};

onMounted(async () => {
    await getData();
    await auditDocumentTemplateStore.list();
});
</script>
